import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
    getCampaignLiveData,
    getCampaign,
    LiveCallLogEntry,
    LiveDataParams,
    SuccessMetric
} from '../../services/api';
import { HiOutlineSearch, HiOutlineRefresh, HiOutlineFilter, HiOutlineX } from 'react-icons/hi';

// Auto-refresh interval (90 seconds)
const AUTO_REFRESH_INTERVAL = 90000;
const ITEMS_PER_PAGE = 50;

interface LiveDataTableProps {
    campaignId: string;
}

interface SortConfig {
    field: string;
    direction: 'asc' | 'desc';
}

interface FilterState {
    dateFrom?: string;
    dateTo?: string;
    minDuration?: number;
    maxDuration?: number;
    callStatus?: string;
    outcome?: string;
}

const LiveDataTable: React.FC<LiveDataTableProps> = ({ campaignId }) => {
    // State management
    const [allData, setAllData] = useState<LiveCallLogEntry[]>([]);
    const [currentOffset, setCurrentOffset] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [sortConfig, setSortConfig] = useState<SortConfig>({
        field: 'created_at',
        direction: 'desc'
    });

    // Split filter state: valid (for query) vs pending (for inputs)
    const [pendingFilters, setPendingFilters] = useState<FilterState>({});
    const [activeFilters, setActiveFilters] = useState<FilterState>({});

    const [showFilters, setShowFilters] = useState(false);
    const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true);

    // Refs
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const autoRefreshTimerRef = useRef<NodeJS.Timeout | null>(null);
    const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const filterTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Filter hover handlers with delay
    const handleFilterMouseEnter = () => {
        if (filterTimeoutRef.current) {
            clearTimeout(filterTimeoutRef.current);
            filterTimeoutRef.current = null;
        }
        setShowFilters(true);
    };

    const handleFilterMouseLeave = () => {
        filterTimeoutRef.current = setTimeout(() => {
            setShowFilters(false);
        }, 300); // 300ms delay before closing
    };

    // Debounce search input
    useEffect(() => {
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }
        if (filterTimeoutRef.current) {
            clearTimeout(filterTimeoutRef.current);
        }

        searchTimeoutRef.current = setTimeout(() => {
            setDebouncedSearch(searchTerm);
            setCurrentOffset(0); // Reset pagination on search
        }, 600);

        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, [searchTerm]);

    // Build query params
    const queryParams: LiveDataParams = {
        limit: ITEMS_PER_PAGE,
        offset: currentOffset,
        search: debouncedSearch || undefined,
        sortBy: sortConfig.field,
        sortOrder: sortConfig.direction,
        ...activeFilters,
    };

    // Fetch campaign details for success metrics
    const { data: campaignData } = useQuery({
        queryKey: ['campaign', campaignId],
        queryFn: () => getCampaign(campaignId),
        staleTime: 300000,
    });

    const successMetrics = campaignData?.success_metrics || [];

    // Keep previous data to avoid flicker
    const { data, isLoading, error, isFetching, refetch } = useQuery({
        queryKey: ['campaignLiveData', campaignId, queryParams],
        queryFn: () => getCampaignLiveData(campaignId, queryParams),
        refetchOnMount: true,
        refetchOnWindowFocus: false,
        staleTime: 60000,
        placeholderData: (previousData) => previousData,
    });

    // Update data when fetch completes
    useEffect(() => {
        if (data) {
            if (currentOffset === 0) {
                setAllData(data.data);
            } else {
                setAllData(prev => {
                    // Create a map by call_log_id to prevent duplicates
                    const existingIds = new Set(prev.map(item => item.call_log_id));
                    const newItems = data.data.filter(item => !existingIds.has(item.call_log_id));
                    return [...prev, ...newItems];
                });
            }
            setHasMore(data.has_more);
            setLastUpdated(new Date());
            setIsLoadingMore(false);
        }
    }, [data, currentOffset]);

    // Auto-refresh logic
    useEffect(() => {
        if (!autoRefreshEnabled) return;

        // Clear existing timer
        if (autoRefreshTimerRef.current) {
            clearTimeout(autoRefreshTimerRef.current);
        }

        // Set new timer
        autoRefreshTimerRef.current = setTimeout(() => {
            // Only auto-refresh if user is not typing and at top of scroll
            const isAtTop = (scrollContainerRef.current?.scrollTop || 0) < 100;
            const isTyping = searchTerm !== debouncedSearch;

            if (isAtTop && !isTyping) {
                handleRefresh();
            }
        }, AUTO_REFRESH_INTERVAL);

        return () => {
            if (autoRefreshTimerRef.current) {
                clearTimeout(autoRefreshTimerRef.current);
            }
        };
    }, [lastUpdated, autoRefreshEnabled, searchTerm, debouncedSearch]);

    // Pause auto-refresh when typing
    useEffect(() => {
        if (searchTerm !== debouncedSearch) {
            setAutoRefreshEnabled(false);
        } else {
            setAutoRefreshEnabled(true);
        }
    }, [searchTerm, debouncedSearch]);

    // Manual refresh
    const handleRefresh = useCallback(() => {
        setCurrentOffset(0);
        // Do not clear data immediately to prevent flicker/empty state
        // setAllData([]); 
        refetch();
    }, [refetch]);

    // Load more (infinite scroll)
    const handleLoadMore = useCallback(() => {
        if (!isLoadingMore && hasMore && !isFetching) {
            setIsLoadingMore(true);
            setCurrentOffset(prev => prev + ITEMS_PER_PAGE);
        }
    }, [isLoadingMore, hasMore, isFetching]);

    // Detect scroll to bottom
    const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
        const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;

        // Load more when scrolled to 80%
        if (scrollPercentage > 0.8) {
            handleLoadMore();
        }
    }, [handleLoadMore]);

    // Sort handler
    const handleSort = (field: string) => {
        setSortConfig(prev => ({
            field,
            direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
        setCurrentOffset(0);
    };

    // Apply filters
    const handleApplyFilters = () => {
        setCurrentOffset(0);
        setActiveFilters(pendingFilters);
        setShowFilters(false);
    };

    // Clear filters
    const handleClearFilters = () => {
        setPendingFilters({});
        setActiveFilters({});
        setCurrentOffset(0);
        setShowFilters(false);
    };

    // Count active filters
    const activeFilterCount = Object.values(activeFilters).filter(v => v !== undefined && v !== '').length;

    // Flatten analytics data for a row
    const getFlattenedValue = (log: LiveCallLogEntry, slug: string) => {
        const analytics = log.analytics;
        if (!analytics || !analytics.data) return '-';

        const successMetricsData = analytics.data.success_metrics;
        if (!successMetricsData) return '-';

        // Find the metric ID for this slug
        const metricId = successMetrics.find(m => m.slug === slug)?.id;
        if (!metricId) return '-';

        const value = successMetricsData[metricId];
        if (value === undefined || value === null) return '-';

        if (typeof value === 'object' && value.answer !== undefined) {
            return String(value.answer);
        }
        return String(value);
    };

    const getSummary = (log: LiveCallLogEntry) => {
        return log.analytics?.data?.summary || '-';
    };

    // Format duration
    const formatDuration = (seconds: number): string => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}m ${secs}s`;
    };

    // Format timestamp
    const formatTimestamp = (timestamp: string): string => {
        return new Date(timestamp).toLocaleString();
    };

    // Get time since last update
    const getTimeSinceUpdate = (): string => {
        const seconds = Math.floor((new Date().getTime() - lastUpdated.getTime()) / 1000);
        if (seconds < 60) return `${seconds}s ago`;
        const minutes = Math.floor(seconds / 60);
        if (minutes < 60) return `${minutes}m ago`;
        const hours = Math.floor(minutes / 60);
        return `${hours}h ago`;
    };

    // Render sort icon
    const renderSortIcon = (field: string) => {
        if (sortConfig.field !== field) {
            return <span className="opacity-30">↕</span>;
        }
        return sortConfig.direction === 'asc' ? <span>↑</span> : <span>↓</span>;
    };

    return (
        <div className="h-full flex flex-col bg-[var(--card)] rounded-lg border border-[var(--border)]">
            {/* Header Section */}
            <div className="p-4 border-b border-[var(--border)] space-y-3">
                {/* Top Row: Search and Actions */}
                <div className="flex gap-3 items-center">
                    {/* Search */}
                    <div className="flex-1 relative">
                        <HiOutlineSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[var(--muted-foreground)]" />
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search name or phone number..."
                            className="w-full pl-10 pr-4 py-2 bg-[var(--background)] border border-[var(--border)] rounded-md text-[var(--foreground)] placeholder:text-[var(--muted-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--primary)]"
                        />
                        {searchTerm && (
                            <button
                                onClick={() => setSearchTerm('')}
                                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[var(--muted-foreground)] hover:text-[var(--foreground)]"
                            >
                                <HiOutlineX className="w-4 h-4" />
                            </button>
                        )}
                    </div>

                    {/* Filter Button Container with Hover + Absolute Dropdown */}
                    <div
                        className="relative z-20"
                        onMouseEnter={handleFilterMouseEnter}
                        onMouseLeave={handleFilterMouseLeave}
                    >
                        <button
                            className="flex items-center gap-2 px-4 py-2 bg-[var(--secondary)] text-[var(--secondary-foreground)] rounded-md hover:bg-opacity-80 transition-colors"
                        >
                            <HiOutlineFilter className="w-4 h-4" />
                            <span>Filters</span>
                            {activeFilterCount > 0 && (
                                <span className="absolute -top-1 -right-1 bg-[var(--primary)] text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                                    {activeFilterCount}
                                </span>
                            )}
                        </button>

                        {/* Filter Panel (Absolute Dropdown) */}
                        {showFilters && (
                            // Wrapper with pt-2 acts as invisible bridge to prevent mouseleave when crossing the gap
                            <div className="absolute right-0 top-full pt-2 w-96 z-30">
                                <div className="p-4 bg-[#1c6b49] text-white rounded-md shadow-xl border border-[var(--border)]">
                                    <div className="grid grid-cols-2 gap-3">
                                        {/* Date From */}
                                        <div>
                                            <label className="block text-xs font-medium mb-1 opacity-90">Date From</label>
                                            <input
                                                type="datetime-local"
                                                value={pendingFilters.dateFrom || ''}
                                                onChange={(e) => setPendingFilters(prev => ({ ...prev, dateFrom: e.target.value }))}
                                                className="w-full px-2 py-1 text-sm bg-white/10 border border-white/20 rounded text-white placeholder-white/50 focus:outline-none focus:ring-1 focus:ring-white/50"
                                            />
                                        </div>

                                        {/* Date To */}
                                        <div>
                                            <label className="block text-xs font-medium mb-1 opacity-90">Date To</label>
                                            <input
                                                type="datetime-local"
                                                value={pendingFilters.dateTo || ''}
                                                onChange={(e) => setPendingFilters(prev => ({ ...prev, dateTo: e.target.value }))}
                                                className="w-full px-2 py-1 text-sm bg-white/10 border border-white/20 rounded text-white placeholder-white/50 focus:outline-none focus:ring-1 focus:ring-white/50"
                                            />
                                        </div>

                                        {/* Min Duration */}
                                        <div>
                                            <label className="block text-xs font-medium mb-1 opacity-90">Min Duration (s)</label>
                                            <input
                                                type="number"
                                                value={pendingFilters.minDuration || ''}
                                                onChange={(e) => setPendingFilters(prev => ({ ...prev, minDuration: e.target.value ? Number(e.target.value) : undefined }))}
                                                placeholder="0"
                                                className="w-full px-2 py-1 text-sm bg-white/10 border border-white/20 rounded text-white placeholder-white/50 focus:outline-none focus:ring-1 focus:ring-white/50"
                                            />
                                        </div>

                                        {/* Max Duration */}
                                        <div>
                                            <label className="block text-xs font-medium mb-1 opacity-90">Max Duration (s)</label>
                                            <input
                                                type="number"
                                                value={pendingFilters.maxDuration || ''}
                                                onChange={(e) => setPendingFilters(prev => ({ ...prev, maxDuration: e.target.value ? Number(e.target.value) : undefined }))}
                                                placeholder="3600"
                                                className="w-full px-2 py-1 text-sm bg-white/10 border border-white/20 rounded text-white placeholder-white/50 focus:outline-none focus:ring-1 focus:ring-white/50"
                                            />
                                        </div>

                                        {/* Call Status */}
                                        <div>
                                            <label className="block text-xs font-medium mb-1 opacity-90">Status</label>
                                            <select
                                                value={pendingFilters.callStatus || ''}
                                                onChange={(e) => setPendingFilters(prev => ({ ...prev, callStatus: e.target.value || undefined }))}
                                                className="w-full px-2 py-1 text-sm bg-white/10 border border-white/20 rounded text-white focus:outline-none focus:ring-1 focus:ring-white/50 [&>option]:text-black"
                                            >
                                                <option value="">All</option>
                                                <option value="completed">Completed</option>
                                                <option value="failed">Failed</option>
                                                <option value="ringing">Ringing</option>
                                                <option value="missed">Missed</option>
                                                <option value="rejected">Rejected</option>
                                            </select>
                                        </div>

                                        {/* Outcome */}
                                        <div>
                                            <label className="block text-xs font-medium mb-1 opacity-90">Outcome</label>
                                            <select
                                                value={pendingFilters.outcome || ''}
                                                onChange={(e) => setPendingFilters(prev => ({ ...prev, outcome: e.target.value || undefined }))}
                                                className="w-full px-2 py-1 text-sm bg-white/10 border border-white/20 rounded text-white focus:outline-none focus:ring-1 focus:ring-white/50 [&>option]:text-black"
                                            >
                                                <option value="">All</option>
                                                <option value="answered">Answered</option>
                                                <option value="voicemail">Voicemail</option>
                                                <option value="no_answer">No Answer</option>
                                                <option value="busy">Busy</option>
                                                <option value="failed">Failed</option>
                                            </select>
                                        </div>
                                    </div>

                                    {/* Filter Actions */}
                                    <div className="flex gap-2 justify-end mt-4">
                                        <button
                                            onClick={handleClearFilters}
                                            className="px-3 py-1.5 text-xs bg-white/10 text-white rounded hover:bg-white/20 border border-white/20"
                                        >
                                            Clear
                                        </button>
                                        <button
                                            onClick={handleApplyFilters}
                                            className="px-3 py-1.5 text-xs bg-white text-[#1c6b49] font-medium rounded hover:bg-opacity-90 shadow-sm"
                                        >
                                            Apply
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Refresh Button */}
                    <button
                        onClick={handleRefresh}
                        disabled={isFetching}
                        className="flex items-center gap-2 px-4 py-2 bg-[var(--primary)] text-white rounded-md hover:bg-opacity-90 transition-colors disabled:opacity-50"
                    >
                        <HiOutlineRefresh className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
                        <span>Refresh</span>
                    </button>
                </div>

                {/* Last Updated */}
                <div className="text-sm text-[var(--muted-foreground)]">
                    Last updated: {getTimeSinceUpdate()}
                </div>
            </div>

            {/* Table Container with Scroll */}
            <div
                ref={scrollContainerRef}
                onScroll={handleScroll}
                className="flex-1 overflow-auto"
            >
                <table className="w-full">
                    {/* Table Header (Sticky) */}
                    <thead className="sticky top-0 bg-[#1c6b49] text-white z-10 shadow-sm">
                        <tr className="border-b border-[var(--border)]">
                            <th
                                onClick={() => handleSort('phone')}
                                className="px-4 py-3 text-left text-sm font-semibold text-white cursor-pointer hover:bg-opacity-80 transition-colors"
                            >
                                <div className="flex items-center gap-2">
                                    Phone Number {renderSortIcon('phone')}
                                </div>
                            </th>
                            <th
                                onClick={() => handleSort('name')}
                                className="px-4 py-3 text-left text-sm font-semibold text-white cursor-pointer hover:bg-opacity-80 transition-colors"
                            >
                                <div className="flex items-center gap-2">
                                    Name {renderSortIcon('name')}
                                </div>
                            </th>
                            <th
                                onClick={() => handleSort('created_at')}
                                className="px-4 py-3 text-left text-sm font-semibold text-white cursor-pointer hover:bg-opacity-80 transition-colors"
                            >
                                <div className="flex items-center gap-2">
                                    Date/Time {renderSortIcon('created_at')}
                                </div>
                            </th>
                            <th
                                onClick={() => handleSort('status')}
                                className="px-4 py-3 text-left text-sm font-semibold text-white cursor-pointer hover:bg-opacity-80 transition-colors"
                            >
                                <div className="flex items-center gap-2">
                                    Status {renderSortIcon('status')}
                                </div>
                            </th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-white">
                                Attempts
                            </th>
                            <th
                                onClick={() => handleSort('outcome')}
                                className="px-4 py-3 text-left text-sm font-semibold text-white cursor-pointer hover:bg-opacity-80 transition-colors"
                            >
                                <div className="flex items-center gap-2">
                                    Outcome {renderSortIcon('outcome')}
                                </div>
                            </th>
                            <th
                                onClick={() => handleSort('duration')}
                                className="px-4 py-3 text-left text-sm font-semibold text-white cursor-pointer hover:bg-opacity-80 transition-colors"
                            >
                                <div className="flex items-center gap-2">
                                    Duration {renderSortIcon('duration')}
                                </div>
                            </th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-white">
                                Recording
                            </th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-white">
                                Transcript
                            </th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-white">
                                <div className="flex items-center gap-2">
                                    Disconnected By
                                </div>
                            </th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-white min-w-[200px]">
                                Summary
                            </th>

                            {/* Analytics Columns (Dynamic from success_metrics) */}
                            {successMetrics.map((metric) => (
                                <th key={metric.slug} className="px-4 py-3 text-left text-sm font-semibold text-white whitespace-nowrap">
                                    {metric.slug.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                                </th>
                            ))}
                        </tr>
                    </thead>

                    {/* Table Body */}
                    <tbody>
                        {isLoading && currentOffset === 0 ? (
                            // Initial loading skeleton
                            Array.from({ length: 10 }).map((_, i) => (
                                <tr key={i} className="border-b border-[var(--border)] animate-pulse">
                                    <td className="px-4 py-3"><div className="h-4 bg-gray-500/20 rounded"></div></td>
                                    <td className="px-4 py-3"><div className="h-4 bg-gray-500/20 rounded"></div></td>
                                    <td className="px-4 py-3"><div className="h-4 bg-gray-500/20 rounded"></div></td>
                                    <td className="px-4 py-3"><div className="h-4 bg-gray-500/20 rounded"></div></td>
                                    <td className="px-4 py-3"><div className="h-4 bg-gray-500/20 rounded"></div></td>
                                    <td className="px-4 py-3"><div className="h-4 bg-gray-500/20 rounded"></div></td>
                                    <td className="px-4 py-3"><div className="h-4 bg-gray-500/20 rounded"></div></td>
                                    <td className="px-4 py-3"><div className="h-4 bg-gray-500/20 rounded"></div></td>
                                    <td className="px-4 py-3"><div className="h-4 bg-gray-500/20 rounded"></div></td>
                                    <td className="px-4 py-3"><div className="h-4 bg-gray-500/20 rounded"></div></td>
                                    <td className="px-4 py-3"><div className="h-4 bg-gray-500/20 rounded"></div></td>
                                    {successMetrics.map((_, idx) => (
                                        <td key={idx} className="px-4 py-3"><div className="h-4 bg-gray-500/20 rounded"></div></td>
                                    ))}
                                </tr>
                            ))
                        ) : allData.length === 0 ? (
                            // Empty state
                            <tr>
                                <td colSpan={11 + successMetrics.length} className="px-4 py-12 text-center text-[var(--muted-foreground)]">
                                    {debouncedSearch || activeFilterCount > 0
                                        ? "No calls match your search or filters"
                                        : "No call logs available"}
                                </td>
                            </tr>
                        ) : (
                            // Data rows
                            <>
                                {allData.map((log, index) => (
                                    <tr
                                        key={`${log.call_log_id}-${index}`}
                                        className="border-b border-[var(--border)] transition-all duration-200 hover:shadow-md hover:scale-[1.002] bg-[var(--card)]"
                                    >
                                        <td className="px-4 py-3 text-sm">{log.phone_number}</td>
                                        <td className="px-4 py-3 text-sm font-bold">
                                            {log.first_name || log.last_name
                                                ? `${log.first_name || ''} ${log.last_name || ''}`.trim()
                                                : '-'}
                                        </td>
                                        <td className="px-4 py-3 text-sm">{formatTimestamp(log.created_at)}</td>
                                        <td className="px-4 py-3 text-sm">
                                            <span className={`px-2 py-1 rounded-full text-xs font-medium
                        ${log.call_status === 'completed' ? 'bg-green-500/20 text-green-600' : ''}
                        ${['failed', 'missed'].includes(log.call_status || '') ? 'bg-red-500/20 text-red-600' : ''}
                        ${log.call_status === 'ringing' ? 'bg-yellow-400 text-black' : ''}
                        ${log.call_status === 'rejected' ? 'bg-orange-500/20 text-orange-600' : ''}
                        ${log.call_status === 'calling' ? 'bg-blue-500/20 text-blue-600' : ''}
                      `}>
                                                {log.call_status}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-sm">{log.call_attempts}</td>
                                        <td className="px-4 py-3 text-sm">{log.call_outcome || '-'}</td>
                                        <td className="px-4 py-3 text-sm">{formatDuration(log.duration)}</td>
                                        <td className="px-4 py-3 text-sm">
                                            {log.recording_url ? (
                                                <a
                                                    href={log.recording_url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-[var(--primary)] hover:underline"
                                                >
                                                    Listen
                                                </a>
                                            ) : '-'}
                                        </td>
                                        <td className="px-4 py-3 text-sm">
                                            {log.transcript_url ? (
                                                <a
                                                    href={log.transcript_url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-[var(--primary)] hover:underline"
                                                >
                                                    View
                                                </a>
                                            ) : '-'}
                                        </td>
                                        <td className="px-4 py-3 text-sm">{log.disconnected_by || '-'}</td>

                                        {/* Summary Column - Expandable on hover */}
                                        <td className="px-4 py-3 text-sm max-w-[300px] group relative">
                                            <div className="truncate cursor-help" title={getSummary(log)}>
                                                {getSummary(log)}
                                            </div>
                                            {getSummary(log) !== '-' && (
                                                <div className="hidden group-hover:block absolute left-0 top-full mt-1 z-[100] p-4 bg-gray-900 text-white text-sm rounded-lg shadow-2xl min-w-[320px] max-w-[600px] border border-white/20 whitespace-normal shadow-black">
                                                    {getSummary(log)}
                                                </div>
                                            )}
                                        </td>

                                        {/* Render Success Metrics Data */}
                                        {successMetrics.map((metric) => (
                                            <td key={metric.slug} className="px-4 py-3 text-sm font-bold whitespace-nowrap">
                                                {getFlattenedValue(log, metric.slug)}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </>
                        )}
                    </tbody>
                </table>

                {/* Loading more indicator - Sticky Centered */}
                {isLoadingMore && (
                    <div className="sticky left-1/2 -translate-x-1/2 py-4 text-center text-[var(--muted-foreground)] w-fit bg-[var(--card)]/80 backdrop-blur-sm rounded-full px-4 my-2 shadow-sm border border-[var(--border)]">
                        <HiOutlineRefresh className="w-5 h-5 animate-spin inline-block mr-2" />
                        Loading more...
                    </div>
                )}

                {/* No more data indicator - Sticky Centered */}
                {!hasMore && allData.length > 0 && (
                    <div className="sticky left-1/2 -translate-x-1/2 py-4 text-center text-[var(--muted-foreground)] w-fit">
                        All data loaded
                    </div>
                )}
            </div>

            {/* Error State */}
            {error && (
                <div className="p-4 bg-red-500/10 border-t border-red-500/20">
                    <p className="text-red-600 text-sm">
                        Error loading data: {error instanceof Error ? error.message : 'Unknown error'}
                    </p>
                </div>
            )}
        </div>
    );
};

export default LiveDataTable;
